/* BD PRODUCT REVIEWS UGC — mobile rail + scroll progress indicator
 *
 * Mobile   Flickity rail snapping to the start of each card.
 * Desktop  static 3-up grid; Flickity deactivates itself and the indicator hides.
 * No JS    the rail stays a native CSS scroll-snap row (stylesheet only).
 *
 * The breakpoint lives entirely in CSS: Flickity's `watchCSS` reads the
 * `::after { content: 'flickity' }` hook on the rail and activates or
 * deactivates itself on resize. There is no second slider path in here.
 *
 * Indicator performance: `measure()` is the only function that reads layout
 * and it runs on init/resize only. `paint()` is write-only, so a drag never
 * forces a synchronous layout — that interleaved read/write per frame is what
 * made the previous version stutter.
 */
(function () {
  'use strict';

  var HIDDEN_CLASS = 'bd-reviews-ugc__indicator--hidden';
  var MIN_THUMB_WIDTH = 24;
  var RESIZE_DELAY = 150;

  var FLICKITY_OPTIONS = {
    cellAlign: 'left',
    contain: true,
    watchCSS: true,
    groupCells: false,
    adaptiveHeight: false,
    pageDots: false,
    prevNextButtons: false,
    // Cells are a fixed 300px, so absolute positions are steadier than the
    // percentage default, which is re-derived from the viewport on resize.
    percentPosition: false,
    // Flickity's default of 3px claims almost-vertical swipes as a drag,
    // which reads as the rail fighting the page scroll.
    dragThreshold: 10
  };

  /* Mirrors Flickity's own scroll-progress formula so a re-sync lands on the
     same value the `scroll` event would report. */
  function progressOf(slider) {
    var first = slider.slides && slider.slides[0];
    if (!first || !slider.slidesWidth) return 0;
    return (-slider.x - first.target) / slider.slidesWidth;
  }

  function createIndicator(root) {
    var el = root.querySelector('[data-reviews-indicator]');
    var track = root.querySelector('[data-reviews-track]');
    var thumb = root.querySelector('[data-reviews-thumb]');
    var travel = 0;
    var live = false;

    /* The indicator is visible by default via CSS, at the design's resting
       state (thumb = 1/3 of the track, at the start) — that stays correct
       with no JS at all for the normal 3-card case. `hide()` only covers the
       genuine edge case of a single card, where there is nothing to
       indicate; it is never used to gate the common path on JS/Flickity
       succeeding. */
    function hide() {
      live = false;
      if (el) el.classList.add(HIDDEN_CLASS);
    }

    function show() {
      if (el) el.classList.remove(HIDDEN_CLASS);
    }

    /* Reads layout — init and resize only. `steps` is the number of snap
       positions; the design sizes the thumb as one step of the track. */
    function measure(steps) {
      if (!el || !track || !thumb) return;
      if (!steps || steps < 2) return hide();

      show();

      var trackWidth = track.clientWidth;
      if (trackWidth <= 0) return hide();

      var thumbWidth = Math.max(Math.round(trackWidth / steps), MIN_THUMB_WIDTH);

      thumb.style.width = thumbWidth + 'px';
      travel = trackWidth - thumbWidth;
      live = true;
    }

    /* Write-only — safe to call on every frame of a drag. */
    function paint(progress) {
      if (!live) return;
      var p = progress < 0 ? 0 : progress > 1 ? 1 : progress;
      thumb.style.transform = 'translate3d(' + p * travel + 'px, 0, 0)';
    }

    return { measure: measure, paint: paint, hide: hide };
  }

  var FLICKITY_ENABLED_CLASS = 'flickity-enabled';

  /* Drives the indicator off the rail's own native `scroll` event. This is
     the resilience layer: Flickity's dynamic import can be slow, blocked, or
     fail outright (its own chunk-loading path — the same one the product
     gallery uses — has no guaranteed activation time), and `watchCSS` itself
     only ever activates Flickity below the desktop breakpoint. Whenever
     Flickity is not the one driving the rail, this makes sure the indicator
     still tracks the native CSS scroll-snap fallback instead of sitting
     inert at its CSS resting position.
     Read-only in the scroll handler (`scrollLeft`/`scrollWidth`/
     `clientWidth`), rAF-batched, and a strict no-op once Flickity takes over
     — so it never doubles up with Flickity's own event and never reintroduces
     the read/write-per-frame thrash the drag used to have. */
  function initNativeFallback(rail, indicator, cardCount) {
    var frame = null;

    function isFlickityDriving() {
      return rail.classList.contains(FLICKITY_ENABLED_CLASS);
    }

    function update() {
      frame = null;
      if (isFlickityDriving()) return;

      var scrollable = rail.scrollWidth - rail.clientWidth;

      if (scrollable <= 1) {
        indicator.measure(0);
        return;
      }

      indicator.measure(cardCount);
      indicator.paint(rail.scrollLeft / scrollable);
    }

    function requestUpdate() {
      if (frame !== null || isFlickityDriving()) return;
      frame = window.requestAnimationFrame(update);
    }

    rail.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(requestUpdate).observe(rail);
    }

    update();
  }

  function initRail(root) {
    var rail = root.querySelector('[data-reviews-rail]');
    if (!rail) return;

    var indicator = createIndicator(root);
    var cardCount = rail.querySelectorAll('.bd-reviews-ugc__card').length;

    initNativeFallback(rail, indicator, cardCount);

    if (typeof flu === 'undefined' || !flu.chunks || !flu.chunks.flickity) return;

    import(flu.chunks.flickity)
      .then(function (chunk) {
        var Flickity = chunk.Flickity;
        if (!Flickity) return;

        var slider = Flickity.data(rail) || new Flickity(rail, FLICKITY_OPTIONS);

        function sync() {
          if (!slider.isActive) return indicator.hide();
          indicator.measure(slider.slides ? slider.slides.length : 0);
          indicator.paint(progressOf(slider));
        }

        slider.on('scroll', indicator.paint);
        slider.on('ready', sync);
        slider.on('activate', sync);
        slider.on('deactivate', indicator.hide);

        // Flickity emits no resize event; watchCSS handles the breakpoint,
        // this just re-measures the track.
        var resizeTimer = null;
        window.addEventListener('resize', function () {
          if (resizeTimer) clearTimeout(resizeTimer);
          resizeTimer = setTimeout(sync, RESIZE_DELAY);
        });

        sync();
      })
      .catch(function (error) {
        console.error('BD Product Reviews UGC: Flickity failed to load', error);
      });
  }

  function initCta(root) {
    root.querySelectorAll('[data-reviews-cta]').forEach(function (button) {
      button.addEventListener('click', function () {
        var selector = button.getAttribute('data-scroll-target');
        if (!selector) return;

        var target = null;
        try {
          target = document.querySelector(selector);
        } catch (error) {
          return;
        }

        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-bd-reviews-ugc]').forEach(function (root) {
      if (root.dataset.bdReviewsUgcBound === '1') return;
      root.dataset.bdReviewsUgcBound = '1';

      initCta(root);
      initRail(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
